<section class="py-3 balance-transfers-section">
  <div class="container-fluid">

    <!-- Top Navigation & Header -->
    <div class="mb-4">
      <a [routerLink]="['/balance-account']" class="back-link cursor-pointer d-inline-flex align-items-center gap-2 text-decoration-none text-muted fw-semibold small mb-2">
        <fa-icon [icon]="faArrowLeft"></fa-icon>
        <span>Balance Accounts</span>
      </a>

      <div class="d-flex align-items-start justify-content-between flex-wrap gap-3">
        <app-main-heading
          [heading]="summaryStats?.accountName || accountDetails?.accountName || 'Balance Account'"
          [footer]="formatReference(summaryStats?.accountNumber || accountDetails?.accountNumber) + ' · ' + (summaryStats?.totalNumberOfTransfers || 0) + ' balance transfers'"
          class="m-0">
        </app-main-heading>

        <div class="d-flex align-items-center gap-2 flex-wrap">
          <button class="btn app-btn-outline py-2 px-3 d-flex align-items-center gap-2" (click)="openNewTransferModal()">
            <fa-icon [icon]="faExchangeAlt" class="icon-sm"></fa-icon>
            <span class="fw-semibold">New Balance Transfer</span>
          </button>
          <button class="btn app-btn-primary py-2 px-3 d-flex align-items-center gap-2" (click)="openEditAccountModal()">
            <fa-icon [icon]="faEdit" class="icon-sm"></fa-icon>
            <span class="fw-semibold">Edit Account</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Responsive Layout Cards -->
    <div class="row g-3">

      <!-- 1. Current Balance Card -->
      <div class="col-12 col-md-7 col-lg-8 order-1">
        <div class="card summary-card current-balance-card h-100 d-flex flex-column justify-content-between">
          <div>
            <div class="card-label mb-2">Current balance</div>
            <div class="current-balance-value fw-bold mb-2">
              ₹{{ (summaryStats?.balance ?? accountDetails?.amount ?? 0) | number : '1.0-0' : 'en-IN' }}
            </div>
          </div>
          <div class="card-footer-text">
            Updated from settlements and balance transfers
          </div>
        </div>
      </div>

      <!-- 2. Net Balance Transfers Card -->
      <div class="col-12 col-md-5 col-lg-4 order-2">
        <div class="card summary-card net-balance-card h-100 d-flex flex-column justify-content-between">
          <div>
            <div class="card-label mb-2">
              Net balance transfers
            </div>
            <div class="net-balance-value fw-bold mb-2">
              {{ (summaryStats?.netTransfer || 0) >= 0 ? '+' : '' }} ₹{{ ((summaryStats?.netTransfer || 0) < 0 ? -(summaryStats?.netTransfer || 0) : (summaryStats?.netTransfer || 0)) | number : '1.0-0' : 'en-IN' }}
            </div>
          </div>
          <div class="card-footer-text">
            {{ summaryStats?.transfersThisMonth || 0 }} balance transfers this month
          </div>
        </div>
      </div>

      <!-- 3. Linked Payment Accounts Card (Order 3 on sm, Order 4 on md) -->
      <div class="col-12 col-md-5 col-lg-4 order-3 order-md-4">
        <div class="card custom-card p-4 h-100 d-flex flex-column gap-3">
          <div class="d-flex align-items-center justify-content-between">
            <h5 class="section-title m-0">Payment accounts linked</h5>
          </div>

          <!-- Alert Note -->
          <div class="info-alert-box p-3 rounded-3 d-flex align-items-start gap-2">
            <fa-icon [icon]="faInfoCircle" class="info-icon mt-1"></fa-icon>
            <span class="info-alert-text">
              Only Immediate and Delayed payment accounts appear here. Manual accounts choose a balance account when creating a settlement.
            </span>
          </div>

          <!-- Linked Payment Accounts Card List -->
          <div class="custom-table-scroll flex-grow-1" infiniteScroll [scrollWindow]="false" [infiniteScrollDistance]="2" [infiniteScrollThrottle]="500" (scrolled)="onScrollPaymentAccounts()">
            <div class="d-flex flex-column gap-2">
              @for (item of paymentAccounts; track item._id; let idx = $index) {
                <div class="linked-account-card-item p-3 rounded-3 border d-flex flex-column gap-2">
                  <!-- Top Row: Top Left Payment Account Badge & Top Right Mode -->
                  <div class="d-flex align-items-center justify-content-between">
                    <span class="account-ref-badge" [ngStyle]="{ 'background-color': getBadgeColor(item._id, idx) + '22', 'color': getBadgeColor(item._id, idx) }">
                      {{ item.accountName }}
                    </span>
                    <span class="payment-mode-cell">{{ item.paymentType || '-' }}</span>
                  </div>

                  <!-- Bottom Row: Bottom Left Settlement Type Badge & Delay Days -->
                  <div class="d-flex align-items-center justify-content-between">
                    @if (item.settlementType === 'Immediate') {
                      <span class="settlement-immediate-badge">Immediate</span>
                    } @else if (item.settlementType === 'Auto' || item.settlementType === 'Delayed') {
                      <div class="d-flex align-items-center gap-2">
                        <span class="settlement-delayed-badge">Delayed</span>
                        <span class="days-cell fw-bold">{{ item.settlementPeriod || 0 }} days</span>
                      </div>
                    } @else {
                      <span class="text-muted small">{{ item.settlementType || '-' }}</span>
                    }
                  </div>
                </div>
              }

              @if (paymentAccountsLoader) {
                <div class="text-center py-3">
                  <output class="spinner-border spinner-border-sm text-primary" aria-hidden="true"></output>
                </div>
              }

              @if (!paymentAccountsLoader && paymentAccountsErrorMessage) {
                <div class="text-center py-3 text-danger small fw-semibold">
                  {{ paymentAccountsErrorMessage }}
                </div>
              }

              @if (!paymentAccountsLoader && !paymentAccountsErrorMessage && paymentAccounts.length === 0) {
                <div class="text-center py-3 text-muted small">
                  No linked payment accounts found.
                </div>
              }
            </div>
          </div>
        </div>
      </div>

      <!-- 4. Balance Transfers Card (Order 4 on sm, Order 3 on md) -->
      <div class="col-12 col-md-7 col-lg-8 order-4 order-md-3">
        <div class="card custom-card p-4 d-flex flex-column gap-3">
          <div class="d-flex align-items-center justify-content-start flex-wrap">
            <h5 class="section-title m-0">Balance transfers</h5>
          </div>

          <!-- Transfers Table -->
          <div class="card app-table-thick-border shadow-none overflow-hidden">
            <div class="table-responsive custom-table-scroll" infiniteScroll [scrollWindow]="false" [infiniteScrollDistance]="2" [infiniteScrollThrottle]="500" (scrolled)="onScrollTransfers()">
              <table class="table app-table-rounded custom-table align-middle m-0 w-100">
                <thead>
                  <tr class="app-table-header text-nowrap">
                    <th scope="col" style="width: 25%;">Date</th>
                    <th scope="col" style="width: 40%;">To / From</th>
                    <th scope="col" style="width: 20%;" class="text-end">Amount</th>
                    <th scope="col" style="width: 15%;">By</th>
                  </tr>
                </thead>
                <tbody>
                  @for (transfer of transfersList; track transfer._id || $index) {
                    <tr class="app-table-body">
                      <td class="fw-semibold text-dark">
                        {{ (transfer.transferDate || transfer.created) | date:'dd-MM-yyyy':'Asia/Kolkata' }}
                      </td>
                      <td>
                        <div class="d-flex flex-column">
                          <span class="fw-bold text-dark">
                            {{ transfer.type }} - {{ transfer.accountName }}
                          </span>
                          <span class="text-muted small transfer-notes" [title]="transfer.notes || 'Balance transfer'">{{ transfer.notes || 'Balance transfer' }}</span>
                        </div>
                      </td>
                      <td class="text-end fw-bold" [ngClass]="transfer.amount < 0 ? 'text-dark' : 'text-green'">
                        {{ transfer.amount < 0 ? '-' : '+' }} ₹{{ (transfer.amount < 0 ? -transfer.amount : transfer.amount) | number : '1.0-0' : 'en-IN' }}
                      </td>
                      <td class="text-muted small">
                        {{ transfer.createdBy || '-' }}
                      </td>
                    </tr>
                  }

                  @if (transfersLoader) {
                    <tr>
                      <td colspan="4" class="text-center py-4">
                        <output class="spinner-border spinner-border-sm text-primary" aria-hidden="true"></output>
                      </td>
                    </tr>
                  }

                  @if (!transfersLoader && transfersErrorMessage) {
                    <tr>
                      <td colspan="4" class="text-center py-4 text-danger small fw-semibold">
                        {{ transfersErrorMessage }}
                      </td>
                    </tr>
                  }

                  @if (!transfersLoader && !transfersErrorMessage && transfersList.length === 0) {
                    <tr>
                      <td colspan="4" class="text-center py-4 text-muted small">
                        No balance transfers found.
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

    </div>

  </div>
</section>

